import React from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

import BetaDistributorFilter from 'components/upb_viewer_beta/BetaDistributorFilter';
import { NAVIGATION_SIDE_BAR_WIDTH } from 'lib/constants';

import {
  BORDER_2,
  BG_2,
  FG_1,
  FG_3,
  FONT_BODY,
  FONT_TITLE,
  FONT_WEIGHT_SEMIBOLD,
  GLOBAL_NAV_HEIGHT,
  KPI_STRIP_HEIGHT,
  PRIMARY_COLOR,
  PRIMARY_HOVER,
  PRIMARY_LIGHT_RGBA_14,
  RADIUS_SM,
  SHADOW_MD,
  TOPBAR_HEIGHT,
  WHITE,
} from 'components/upb_viewer_beta/css/betaTokens';

/**
 * KpiMetricCard — a single selectable metric card in the strip.
 */
function KpiMetricCard({ metric, isActive, onMetricChange, loading }) {
  return (
    <button
      className={css(styles.kpi, isActive && styles.kpiActive)}
      onClick={() => onMetricChange && onMetricChange(metric.key)}
      title={metric.label}
      type="button"
    >
      <span className={css(styles.kpiLabel, isActive && styles.kpiLabelActive)}>
        {metric.label}
        {metric.tooltip && (
          <span className={css(styles.info)} data-tip={metric.tooltip}>
            <FontAwesomeIcon icon={faInfoCircle} className={css(styles.infoIcon)} />
          </span>
        )}
      </span>
      <span className={css(styles.kpiValue)}>{loading ? '—' : metric.value ?? '—'}</span>
    </button>
  );
}

KpiMetricCard.propTypes = {
  metric: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    tooltip: PropTypes.string,
  }).isRequired,
  isActive: PropTypes.bool,
  onMetricChange: PropTypes.func,
  loading: PropTypes.bool,
};

KpiMetricCard.defaultProps = {
  isActive: false,
  onMetricChange: undefined,
  loading: false,
};

/**
 * BetaKpiStrip — 7-card metric switcher for the UPB Viewer Beta.
 *
 * Props:
 *   metrics       Array<{ key, label, value, tooltip? }>   — the 7 KPI cards
 *   activeMetric  string                                   — key of selected card
 *   onMetricChange (key: string) => void                   — card click handler
 *   loading       boolean                                  — show skeleton values
 */
function BetaKpiStrip({
  metrics,
  activeMetric,
  onMetricChange,
  loading,
  vendorId,
  thirdPartyTag,
  onVendorChange,
  navExpanded,
}) {
  return (
    <div className={css(styles.strip, navExpanded && styles.stripNavOpen)}>
      {/* Optional leading distributor filter — the UPB Viewer Beta keeps it in
          the strip; the MSV renders it in the content header card instead
          (per mock) and simply doesn't pass onVendorChange. */}
      {onVendorChange && (
        <BetaDistributorFilter
          value={vendorId}
          thirdPartyTag={thirdPartyTag}
          onChange={onVendorChange}
        />
      )}
      {metrics.map(metric => (
        <KpiMetricCard
          key={metric.key}
          metric={metric}
          isActive={metric.key === activeMetric}
          onMetricChange={onMetricChange}
          loading={loading}
        />
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  stripNavOpen: {
    left: NAVIGATION_SIDE_BAR_WIDTH,
  },

  strip: {
    position: 'fixed',
    top: GLOBAL_NAV_HEIGHT + TOPBAR_HEIGHT,
    left: 0,
    right: 0,
    height: KPI_STRIP_HEIGHT,
    backgroundColor: WHITE,
    borderBottom: `1px solid ${BORDER_2}`,
    display: 'flex',
    alignItems: 'stretch',
    padding: '18px 24px 20px',
    gap: 10,
    zIndex: 99,
  },

  kpi: {
    flex: 1,
    backgroundColor: BG_2,
    border: `1px solid ${BORDER_2}`,
    borderRadius: 10,
    padding: '10px 16px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 4,
    textAlign: 'left',
    transition: 'background 0.15s, border-color 0.15s, box-shadow 0.15s',
    ':hover': {
      backgroundColor: WHITE,
    },
  },

  kpiActive: {
    backgroundColor: WHITE,
    borderColor: PRIMARY_COLOR,
    boxShadow: `0 0 0 3px ${PRIMARY_LIGHT_RGBA_14}`,
  },

  kpiLabel: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    fontSize: 12,
    color: FG_3,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
  },

  kpiLabelActive: {
    color: PRIMARY_HOVER,
  },

  info: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'help',
    ':hover::after': {
      content: 'attr(data-tip)',
      position: 'absolute',
      top: 'calc(100% + 8px)',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: FG_1,
      color: WHITE,
      fontFamily: FONT_BODY,
      fontWeight: 500,
      fontSize: 11,
      padding: '6px 10px',
      borderRadius: RADIUS_SM,
      whiteSpace: 'nowrap',
      zIndex: 40,
      boxShadow: SHADOW_MD,
      pointerEvents: 'none',
    },
  },

  infoIcon: {
    fontSize: 12,
    color: FG_3,
    opacity: 0.5,
    ':hover': {
      opacity: 0.9,
    },
  },

  kpiValue: {
    fontFamily: FONT_TITLE,
    fontWeight: 800,
    fontSize: 22,
    lineHeight: 1.1,
    color: FG_1,
    letterSpacing: '-0.01em',
    fontVariantNumeric: 'tabular-nums',
  },
});

BetaKpiStrip.propTypes = {
  metrics: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.string,
      tooltip: PropTypes.string,
    }),
  ).isRequired,
  activeMetric: PropTypes.string,
  onMetricChange: PropTypes.func,
  loading: PropTypes.bool,
  vendorId: PropTypes.number,
  thirdPartyTag: PropTypes.string,
  onVendorChange: PropTypes.func,
  navExpanded: PropTypes.bool,
};

BetaKpiStrip.defaultProps = {
  loading: false,
  vendorId: null,
  thirdPartyTag: null,
};

export default BetaKpiStrip;
